import axios from 'axios';
import tokenStore from './tokenStore';

// The viewer's IANA timezone. This used to send `timeZoneName: 'long'` — a
// human DISPLAY NAME ("Gulf Standard Time") that PHP's DateTimeZone cannot
// parse, so the backend silently fell back to a wrong zone for every country
// except Saudi/Jordan. See the matching comment in popup-push-frontend's
// src/config/Axios.js. The API now emits UTC ISO-8601 with an explicit offset
// and no longer reads this header; it is kept for legacy backend paths only.
const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

function buildInstance(baseURL) {
  const instance = axios.create({
    baseURL,
    withCredentials: true,
  });

  instance.interceptors.request.use(config => {
    const token = tokenStore.get();
    if (token) config.headers['Authorization'] = `Bearer ${token}`;
    config.headers['Content-Type']  = 'application/json';
    config.headers['Accept']        = 'application/json';
    config.headers['timezone']      = timezone;
    return config;
  });

  instance.interceptors.response.use(
    response => response,
    error => {
      const status = error?.response?.status;
      if (status === 402) {
        window.location.href = '/profile';
      }
      // The auth requests answer 401/403/422 as an ANSWER (wrong password, dead reset link,
      // "not an admin"): the page shows it inline. Only a stale session elsewhere is a reason
      // to clear the token and go back to the sign-in page. (The admin sign-in page is "/",
      // so the old `includes('/login')` exemption never matched it and a wrong password
      // reloaded the page with no message.)
      const reqUrl = error?.config?.url || '';
      const isAuthCall = /users\/(login|forget-password|reset-password|logout)/.test(reqUrl);
      if ((status === 401 || status === 403) && !isAuthCall && !window.location.pathname.includes('/login')) {
        tokenStore.clear();
        window.location.href = '/';
      }
      return Promise.reject(error);
    },
  );

  return instance;
}

// Admin API (requires auth)
export const adminApi = buildInstance(process.env.REACT_APP_ADMIN_API_URL);

// Public API (login, password reset)
export const publicApi = buildInstance(process.env.REACT_APP_API_URL);

export default adminApi;
